import { useRef, useEffect, useState } from 'react';
import Select, {
    components,
    MultiValue,
    MultiValueProps,
    OptionProps,
    GroupBase,
    StylesConfig,
} from 'react-select';
import { CheckIcon } from '@heroicons/react/20/solid';
import { toast } from 'sonner';

interface SearchableMultiSelectOption<T = string> {
    value: T;
    label: string;
}

interface SearchableMultiSelectProps<T = string> {
    label?: string;
    value: T[];
    onChange: (value: T[]) => void;
    options: SearchableMultiSelectOption<T>[];
    error?: string;
    helperText?: string;
    placeholder?: string;
    /** Optional different placeholder for desktop (sm and up) */
    placeholderDesktop?: string;
    /** Size variant - 'sm' (36px) for compact layouts, 'md' (42px) default */
    size?: 'sm' | 'md';
    /** Whether the component is disabled */
    disabled?: boolean;
    /** Tooltip message shown when clicking on disabled component */
    disabledTooltip?: string;
    /** Callback when clicking on disabled component (takes precedence over disabledTooltip) */
    onDisabledClick?: () => void;
    /** Called when the dropdown closes - useful for deferred updates */
    onClose?: () => void;
    /** Single-row compact display showing count instead of individual chips */
    compact?: boolean;
    /** Show checkmarks in dropdown options (default: true) */
    showCheckmarks?: boolean;
}

// Custom option component with optional checkmark
function CustomOption<T>(props: OptionProps<SearchableMultiSelectOption<T>, true, GroupBase<SearchableMultiSelectOption<T>>>) {
    const showCheckmarks = (props.selectProps as { showCheckmarks?: boolean }).showCheckmarks !== false;

    return (
        <components.Option {...props}>
            <div className="flex items-center">
                {showCheckmarks && (
                    <span className="w-5 h-5 mr-2 flex items-center justify-center">
                        {props.isSelected && (
                            <CheckIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        )}
                    </span>
                )}
                <span className={props.isSelected ? 'font-medium' : 'font-normal'}>
                    {props.label}
                </span>
            </div>
        </components.Option>
    );
}

// Custom MultiValue for compact mode - shows count instead of individual chips
function CompactMultiValue<T>(
    props: MultiValueProps<SearchableMultiSelectOption<T>, true, GroupBase<SearchableMultiSelectOption<T>>>
) {
    const { index, getValue } = props;
    const selectedValues = getValue();

    // Only render for the first item
    if (index > 0) return null;

    const count = selectedValues.length;
    const firstLabel = selectedValues[0]?.label || '';

    if (count === 1) {
        return (
            <span className="inline-flex items-center rounded-sm bg-blue-100 dark:bg-blue-900 px-2 py-0.5 text-sm text-blue-800 dark:text-blue-100 max-w-[150px] truncate">
                {firstLabel}
            </span>
        );
    }

    return (
        <span className="inline-flex items-center rounded-sm bg-blue-100 dark:bg-blue-900 px-2 py-0.5 text-sm text-blue-800 dark:text-blue-100">
            {count} selected
        </span>
    );
}

// Hook to detect dark mode
function useDarkMode() {
    const [isDark, setIsDark] = useState(() => {
        if (typeof document !== 'undefined') {
            return document.documentElement.classList.contains('dark');
        }
        return false;
    });

    useEffect(() => {
        const observer = new MutationObserver(() => {
            setIsDark(document.documentElement.classList.contains('dark'));
        });

        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class'],
        });

        return () => observer.disconnect();
    }, []);

    return isDark;
}

// Hook to detect mobile viewport (matches Tailwind sm breakpoint)
function useIsMobile() {
    const [isMobile, setIsMobile] = useState(() =>
        typeof window !== 'undefined' && window.innerWidth < 640
    );

    useEffect(() => {
        const mq = window.matchMedia('(max-width: 639px)');
        const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);

    return isMobile;
}

// Color constants matching the @theme colors in src/index.css
const colors = {
    // Custom theme colors
    dark: '#242A34',
    // Tailwind colors
    gray: {
        100: 'rgb(243 244 246)',
        300: 'rgb(209 213 219)',
        400: 'rgb(156 163 175)',
        500: 'rgb(107 114 128)',
        600: 'rgb(75 85 99)',
        800: 'rgb(31 41 55)',
        900: 'rgb(17 24 39)',
    },
    blue: {
        100: 'rgb(219 234 254)',
        200: 'rgb(191 219 254)',
        400: 'rgb(96 165 250)',
        600: 'rgb(37 99 235)',
        700: 'rgb(29 78 216)',
        800: 'rgb(30 64 175)',
        900: 'rgb(30 58 138)',
    },
    red: {
        500: '#ef4444',
    },
    white: '#ffffff',
};

export function SearchableMultiSelect<T = string>({
    label,
    value,
    onChange,
    options,
    error,
    helperText,
    placeholder = 'Select...',
    placeholderDesktop,
    size = 'md',
    disabled = false,
    disabledTooltip,
    onDisabledClick,
    onClose,
    compact = false,
    showCheckmarks = true,
}: SearchableMultiSelectProps<T>) {
    const wasOpenRef = useRef(false);
    const isDark = useDarkMode();
    const isMobile = useIsMobile();

    // Convert values to react-select format
    const selectedOptions = options.filter((opt) => value.includes(opt.value));

    const handleChange = (newValue: MultiValue<SearchableMultiSelectOption<T>>) => {
        onChange(newValue.map((opt) => opt.value));
    };

    const handleDisabledClick = () => {
        if (disabled) {
            if (onDisabledClick) {
                onDisabledClick();
            } else if (disabledTooltip) {
                toast(disabledTooltip);
            }
        }
    };

    const handleMenuClose = () => {
        if (wasOpenRef.current) {
            onClose?.();
        }
        wasOpenRef.current = false;
    };

    const handleMenuOpen = () => {
        wasOpenRef.current = true;
    };

    // Height based on size
    const minHeight = size === 'sm' ? 36 : 42;

    // Determine placeholder based on screen size (simplified - uses desktop if provided)
    const effectivePlaceholder = placeholderDesktop || placeholder;

    // Custom styles matching the existing design and dark mode
    const customStyles: StylesConfig<SearchableMultiSelectOption<T>, true> = {
        control: (base, state) => ({
            ...base,
            minHeight,
            borderRadius: '0.5rem',
            borderColor: error
                ? colors.red[500]
                : state.isFocused
                    ? colors.blue[600]
                    : isDark ? colors.gray[600] : colors.gray[300],
            backgroundColor: state.isDisabled
                ? (isDark ? colors.gray[900] : colors.gray[100])
                : (isDark ? colors.gray[800] : colors.white),
            boxShadow: state.isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.3)' : 'none',
            '&:hover': {
                borderColor: error
                    ? colors.red[500]
                    : state.isFocused
                        ? colors.blue[600]
                        : isDark ? colors.gray[500] : colors.gray[400],
            },
            cursor: state.isDisabled ? 'not-allowed' : 'pointer',
            opacity: state.isDisabled ? 0.5 : 1,
            flexWrap: compact ? 'nowrap' : 'wrap',
            overflow: compact ? 'hidden' : 'visible',
        }),
        valueContainer: (base) => ({
            ...base,
            padding: '2px 8px',
            gap: '4px',
            flexWrap: compact ? 'nowrap' : 'wrap',
            overflow: compact ? 'hidden' : 'visible',
        }),
        multiValue: (base) => ({
            ...base,
            backgroundColor: isDark ? colors.blue[900] : colors.blue[100],
            borderRadius: '0.25rem',
            flexShrink: compact ? 0 : 1,
        }),
        multiValueLabel: (base) => ({
            ...base,
            color: isDark ? colors.blue[100] : colors.blue[800],
            fontSize: '0.875rem',
            padding: '2px 6px',
        }),
        multiValueRemove: (base) => ({
            ...base,
            color: isDark ? colors.blue[100] : colors.blue[800],
            cursor: 'pointer',
            '&:hover': {
                backgroundColor: isDark ? colors.blue[800] : colors.blue[200],
                color: isDark ? colors.blue[100] : colors.blue[700],
            },
        }),
        menu: (base) => ({
            ...base,
            borderRadius: '0.5rem',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            zIndex: 20,
            overflow: 'hidden',
            backgroundColor: isDark ? colors.dark : colors.white,
            border: isDark ? `1px solid ${colors.gray[600]}` : 'none',
        }),
        menuList: (base) => ({
            ...base,
            padding: 0,
            maxHeight: '240px',
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isFocused
                ? (isDark ? colors.blue[900] : colors.blue[100])
                : (isDark ? colors.dark : colors.white),
            color: state.isFocused
                ? (isDark ? colors.blue[100] : colors.blue[900])
                : (isDark ? colors.gray[100] : colors.gray[900]),
            cursor: 'pointer',
            '&:active': {
                backgroundColor: isDark ? colors.blue[800] : colors.blue[200],
            },
        }),
        placeholder: (base) => ({
            ...base,
            color: colors.gray[400],
        }),
        input: (base) => ({
            ...base,
            color: isDark ? colors.gray[100] : colors.gray[900],
        }),
        indicatorSeparator: () => ({
            display: 'none',
        }),
        dropdownIndicator: (base) => ({
            ...base,
            color: colors.gray[400],
            padding: '8px',
            '&:hover': {
                color: colors.gray[500],
            },
        }),
        clearIndicator: (base) => ({
            ...base,
            color: colors.gray[400],
            padding: '8px',
            '&:hover': {
                color: colors.red[500],
            },
        }),
        noOptionsMessage: (base) => ({
            ...base,
            color: isDark ? colors.gray[400] : colors.gray[500],
        }),
    };

    return (
        <div className="w-full" onClick={disabled ? handleDisabledClick : undefined}>
            {label && (
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {label}
                </label>
            )}
            <Select<SearchableMultiSelectOption<T>, true>
                isMulti
                value={selectedOptions}
                onChange={handleChange}
                options={options}
                isDisabled={disabled}
                placeholder={effectivePlaceholder}
                onMenuClose={handleMenuClose}
                onMenuOpen={handleMenuOpen}
                styles={customStyles}
                classNamePrefix="react-select"
                components={{
                    Option: CustomOption,
                    ...(compact ? { MultiValue: CompactMultiValue } : {}),
                }}
                hideSelectedOptions={false}
                closeMenuOnSelect={false}
                blurInputOnSelect={false}
                isClearable={false}
                isSearchable={!isMobile && options.length > 5}
                noOptionsMessage={() => 'No options found'}
                // @ts-expect-error - custom prop passed to Option via selectProps
                showCheckmarks={showCheckmarks}
            />
            {error && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>}
            {helperText && !error && (
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{helperText}</p>
            )}
        </div>
    );
}
